import React, {ReactNode} from 'react';
import {observer} from 'mobx-react';
import {DiagramMode, Store} from '../../stores/store';
import {TermItem} from './TermItem';
import {stylesheet} from 'typestyle';
import {KPI} from '../../kpi';
import {GroupedTermItem} from './GroupedTermItem';
import {MessageProvider} from '../../i18n/i18n';

interface SelectedTermsProps {
    store: Store;
    t: MessageProvider;
}

export interface GroupedNormalLegendData {
    kpi: KPI;
    searchtermOrProductId: string;
    channelNames?: string[];
    testId?: string;
    variantIds?: string[];
    channelName?: string;
}

export interface GroupedChannelLegendData {
    kpi: KPI;
    channelNames?: string[];
    testId?: string;
    variantIds?: string[];
    channelName?: string;
}

@observer
export default class SelectedTerms extends React.Component<SelectedTermsProps, {}> {
    private groupDiagramLegendDataByAbtestAndVariantId(): {normalData: GroupedNormalLegendData[]; channelData: GroupedChannelLegendData[]} {
        const {requestedChannelDiagrams, requestedNormalDiagrams} = this.props.store.lineDiagramStore;
        const groupedNormalData: GroupedNormalLegendData[] = Object.values(
            requestedNormalDiagrams.reduce((groupedData, singleChannelData) => {
                const key =
                    singleChannelData.kpi + singleChannelData.searchtermOrProductId + singleChannelData.channelName + singleChannelData.testId;
                if (groupedData[key] === undefined) {
                    groupedData[key] = {
                        kpi: singleChannelData.kpi,
                        searchtermOrProductId: singleChannelData.searchtermOrProductId,
                        testId: singleChannelData.testId,
                        channelName: singleChannelData.channelName,
                        variantIds: [],
                    };
                }
                groupedData[key].variantIds.push(singleChannelData.variantId);
                return groupedData;
            }, {})
        );

        const groupedChannelData: GroupedChannelLegendData[] = Object.values(
            requestedChannelDiagrams.reduce((groupedData, singleChannelData) => {
                const key = singleChannelData.kpi + singleChannelData.channelName + singleChannelData.testId;
                if (groupedData[key] === undefined) {
                    groupedData[key] = {
                        kpi: singleChannelData.kpi,
                        channelName: singleChannelData.channelName,
                        testId: singleChannelData.testId,
                        variantIds: [],
                    };
                }
                groupedData[key].variantIds.push(singleChannelData.variantId);
                return groupedData;
            }, {})
        );

        return {
            normalData: groupedNormalData,
            channelData: groupedChannelData,
        };
    }

    private groupDiagramLegendDataByChannel(): {normalData: GroupedNormalLegendData[]; channelData: GroupedChannelLegendData[]} {
        const {requestedChannelDiagrams, requestedNormalDiagrams} = this.props.store.lineDiagramStore;
        const groupedNormalData: GroupedNormalLegendData[] = Object.values(
            requestedNormalDiagrams.reduce((groupedData, singleChannelData) => {
                const key = singleChannelData.kpi + singleChannelData.searchtermOrProductId;
                if (groupedData[key] === undefined) {
                    groupedData[key] = {
                        kpi: singleChannelData.kpi,
                        searchtermOrProductId: singleChannelData.searchtermOrProductId,
                        channelNames: [],
                    };
                }
                groupedData[key].channelNames.push(singleChannelData.channelName);
                return groupedData;
            }, {})
        );

        const groupedChannelData: GroupedChannelLegendData[] = Object.values(
            requestedChannelDiagrams.reduce((groupedData, singleChannelData) => {
                const key = singleChannelData.kpi;
                if (groupedData[key] === undefined) {
                    groupedData[key] = {
                        kpi: singleChannelData.kpi,
                        channelNames: [],
                    };
                }
                groupedData[key].channelNames.push(singleChannelData.channelName);
                return groupedData;
            }, {})
        );

        return {
            normalData: groupedNormalData,
            channelData: groupedChannelData,
        };
    }

    private getGroupedChannelCompareItemsIfNecessary(): {noneRateLegendItems: React.ReactNode[]; rateLegendItems: React.ReactNode[]} {
        const {channelCompare, variantChannelCompare} = this.props.store.compareStore.compareData;
        const {lineDiagramStore, selectorTableStore} = this.props.store;

        const groupedLegendData = ((): {normalData: GroupedNormalLegendData[]; channelData: GroupedChannelLegendData[]} => {
            if (channelCompare) {
                return this.groupDiagramLegendDataByChannel();
            } else if (variantChannelCompare) {
                return this.groupDiagramLegendDataByAbtestAndVariantId();
            }
            return {normalData: [], channelData: []};
        })();

        const columnDef = this.props.store.selectorTableStore.tableColumns();
        const noneRateLegendItems: ReactNode[] = [];
        const rateLegendItems: ReactNode[] = [];

        groupedLegendData.channelData.forEach((groupedChannelData) => {
            const termItem = (
                <GroupedTermItem
                    removeTermFromDiagram={lineDiagramStore.removeTermFromDiagram}
                    getLabelForKpi={selectorTableStore.getLabelForKpi}
                    checkIfGraphIsLoaded={lineDiagramStore.checkIfGraphIsLoaded}
                    checkIfGraphIsSelected={lineDiagramStore.checkIfGraphIsSelected}
                    selectGraph={lineDiagramStore.selectGraph}
                    getDiagramColor={lineDiagramStore.getDiagramColor}
                    isChannel={true}
                    key={groupedChannelData.kpi}
                    kpi={groupedChannelData.kpi}
                    availableChannelNames={groupedChannelData.channelNames}
                    abTestData={
                        groupedChannelData.variantIds && groupedChannelData.channelName && groupedChannelData.testId
                            ? {
                                  availableVariants: groupedChannelData.variantIds,
                                  channelName: groupedChannelData.channelName,
                                  testId: groupedChannelData.testId,
                              }
                            : undefined
                    }
                />
            );
            if (columnDef[groupedChannelData.kpi] && columnDef[groupedChannelData.kpi].isRate) {
                rateLegendItems.push(termItem);
            } else {
                noneRateLegendItems.push(termItem);
            }
        });

        groupedLegendData.normalData.forEach((groupedNormalData) => {
            const termItem = (
                <GroupedTermItem
                    removeTermFromDiagram={lineDiagramStore.removeTermFromDiagram}
                    getLabelForKpi={selectorTableStore.getLabelForKpi}
                    checkIfGraphIsLoaded={lineDiagramStore.checkIfGraphIsLoaded}
                    checkIfGraphIsSelected={lineDiagramStore.checkIfGraphIsSelected}
                    selectGraph={lineDiagramStore.selectGraph}
                    getDiagramColor={lineDiagramStore.getDiagramColor}
                    key={groupedNormalData.kpi + groupedNormalData.searchtermOrProductId}
                    kpi={groupedNormalData.kpi}
                    searchtermOrPid={groupedNormalData.searchtermOrProductId}
                    availableChannelNames={groupedNormalData.channelNames}
                    abTestData={
                        groupedNormalData.variantIds && groupedNormalData.channelName && groupedNormalData.testId
                            ? {
                                  availableVariants: groupedNormalData.variantIds,
                                  channelName: groupedNormalData.channelName,
                                  testId: groupedNormalData.testId,
                              }
                            : undefined
                    }
                />
            );
            if (columnDef[groupedNormalData.kpi] && columnDef[groupedNormalData.kpi].isRate) {
                rateLegendItems.push(termItem);
            } else {
                noneRateLegendItems.push(termItem);
            }
        });
        return {noneRateLegendItems, rateLegendItems};
    }

    public getSelectedTerms(): {noneRateLegendItems: React.ReactNode[]; rateLegendItems: React.ReactNode[]} {
        const noneRateLegendItems: ReactNode[] = [];
        const rateLegendItems: ReactNode[] = [];
        const {requestedChannelDiagrams, requestedNormalDiagrams} = this.props.store.lineDiagramStore;
        const columnDef = this.props.store.selectorTableStore.tableColumns();

        requestedChannelDiagrams.forEach((channelLegend) => {
            const {kpi, channelName, title} = channelLegend;
            const termItem = (
                <TermItem
                    key={kpi + ':' + channelName}
                    store={this.props.store}
                    title={channelName}
                    forcedTitle={title ? this.props.t(title) : undefined}
                    secondLine={title ? channelName : undefined}
                    channelName={channelName}
                    kpi={kpi}
                    isChannel={true}
                />
            );
            if (columnDef[kpi] && columnDef[kpi].isRate) {
                rateLegendItems.push(termItem);
            } else {
                noneRateLegendItems.push(termItem);
            }
        });

        requestedNormalDiagrams.forEach((normalLegend) => {
            const {kpi, searchtermOrProductId, channelName, title} = normalLegend;
            const termItem = (
                <TermItem
                    key={channelName + ':' + kpi + ':' + searchtermOrProductId}
                    store={this.props.store}
                    title={searchtermOrProductId}
                    forcedTitle={title}
                    channelName={channelName}
                    kpi={kpi}
                    secondLine={channelName}
                />
            );
            if (columnDef[kpi] && columnDef[kpi].isRate) {
                rateLegendItems.push(termItem);
            } else {
                noneRateLegendItems.push(termItem);
            }
        });
        return {noneRateLegendItems, rateLegendItems};
    }

    public render() {
        const {compareStore} = this.props.store;
        if (this.props.store.diagramMode === DiagramMode.None || !compareStore) {
            return null;
        }

        const diagramLegendItems = compareStore.compareIsEnabled ? this.getGroupedChannelCompareItemsIfNecessary() : this.getSelectedTerms();
        return (
            <div className={itemRowCss.itemRowContainer}>
                {[
                    <div key={1} style={{float: 'left'}}>
                        <div className={itemRowCss.legendContainer}>{diagramLegendItems.noneRateLegendItems}</div>
                    </div>,
                    <div key={2} style={{float: 'right'}}>
                        <div className={itemRowCss.legendContainer}>{diagramLegendItems.rateLegendItems}</div>
                    </div>,
                ]}
            </div>
        );
    }
}

const itemRowCss = stylesheet({
    legendContainer: {
        display: 'flex',
    },
    itemRowContainer: {
        padding: '0 80px 1rem 85px',
        minHeight: 55,
    },
});



// WEBPACK FOOTER //
// ./src/views/Legend/SelectedTerms.tsx